// ProjectMenu — dropdown next to the project name in the top bar.
//
// Hosts the project-level actions that didn't justify their own
// dedicated button:
//   - 切換 project (if more than 1 exists)
//   - 新對話     (clear transcript without touching files)
//   - 新建 project
//   - 改名
//   - 輸出 PDF
//   - 刪除 project (with confirm)
//
// Closes on outside-click or Escape. The "new project" / "rename"
// flows reuse the small inline-prompt pattern from NoProjectScreen
// rather than introducing a real modal — keeps the surface tiny and
// consistent with the rest of the studio's "no chrome until needed"
// style.

import { useEffect, useRef, useState } from 'react';

import { defaultFileStore } from '../core/filestore';
import { exportProjectPdf, triggerDownload } from '../lib/pdf-export';
import { slugifyProjectName } from '../lib/slug';
import { useProjects } from '../state/projects';
import { useTranscript } from '../state/transcript';

type Mode = 'menu' | 'rename' | 'create';

export function ProjectMenu() {
  const projects = useProjects((s) => s.projects);
  const activeProjectName = useProjects((s) => s.activeProjectName);
  const setActive = useProjects((s) => s.setActive);
  const createProject = useProjects((s) => s.createProject);
  const renameProject = useProjects((s) => s.renameProject);
  const deleteProject = useProjects((s) => s.deleteProject);
  const clearTranscript = useTranscript((s) => s.clear);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('menu');
  const [draft, setDraft] = useState('');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (ev: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(ev.target as Node)) {
        close();
      }
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    setMode('menu');
    setDraft('');
    setError(null);
  };

  const startRename = () => {
    if (!activeProjectName) return;
    setDraft(activeProjectName);
    setMode('rename');
  };

  const startCreate = () => {
    setDraft('');
    setMode('create');
  };

  const submitCreate = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    try {
      await createProject(trimmed);
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const submitRename = async () => {
    if (!activeProjectName) return;
    const trimmed = draft.trim();
    if (!trimmed || trimmed === activeProjectName) {
      close();
      return;
    }
    try {
      await renameProject(activeProjectName, trimmed);
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleSwitch = async (name: string) => {
    if (name === activeProjectName) {
      close();
      return;
    }
    try {
      await setActive(name);
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleNewConversation = () => {
    clearTranscript();
    close();
  };

  const handleDelete = async () => {
    if (!activeProjectName) return;
    const ok = window.confirm(
      `確認刪除 project「${activeProjectName}」？包括 OPFS 入面所有 file，唔可以還原。`,
    );
    if (!ok) return;
    try {
      await deleteProject(activeProjectName);
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleExportPdf = async () => {
    if (!activeProjectName) return;
    setError(null);
    setExporting(true);
    try {
      const blob = await exportProjectPdf({
        fileStore: defaultFileStore,
        projectRoot: activeProjectName,
      });
      triggerDownload(blob, `${activeProjectName}.pdf`);
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  };

  const others = projects.filter((p) => p.name !== activeProjectName);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 rounded-md border border-[#222230] bg-[#141419] px-2 py-1 text-[11px] text-neutral-300 hover:border-[#333348]"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="font-medium text-neutral-100">{activeProjectName}</span>
        <span className="text-neutral-500">▾</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-[calc(100%+4px)] z-20 w-72 rounded-md border border-[#222230] bg-[#0d0d12] p-1.5 shadow-lg shadow-black/40"
        >
          {mode === 'menu' && (
            <>
              {others.length > 0 && (
                <>
                  <p className="px-2 py-1 text-[10px] uppercase tracking-[0.12em] text-neutral-600">
                    切換 project
                  </p>
                  <ul>
                    {others.slice(0, 8).map((p) => (
                      <li key={p.name}>
                        <button
                          type="button"
                          onClick={() => void handleSwitch(p.name)}
                          className="block w-full rounded-sm px-2 py-1.5 text-left text-[12px] text-neutral-300 hover:bg-[#1a1a24]"
                        >
                          {p.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                  <Separator />
                </>
              )}

              <MenuItem onClick={handleNewConversation}>新對話（清 transcript）</MenuItem>
              <MenuItem onClick={startCreate}>新建 project</MenuItem>
              <MenuItem onClick={startRename}>改名</MenuItem>
              <MenuItem
                onClick={() => void handleExportPdf()}
                disabled={exporting}
              >
                {exporting ? '輸出中…' : '輸出 PDF'}
              </MenuItem>

              <Separator />

              <MenuItem
                onClick={() => void handleDelete()}
                className="text-[#ff8a8a] hover:bg-[#2a1414]"
              >
                刪除 project
              </MenuItem>
            </>
          )}

          {(mode === 'rename' || mode === 'create') && (
            <div className="p-1.5">
              <p className="text-[10px] uppercase tracking-[0.12em] text-neutral-600">
                {mode === 'rename' ? '新名' : '新 project 名'}
              </p>
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void (mode === 'rename' ? submitRename() : submitCreate());
                  }
                  if (e.key === 'Escape') close();
                }}
                autoFocus
                className="mt-1 w-full rounded-md border border-[#222230] bg-[#141419] px-2 py-1.5 text-[12px] text-neutral-100 focus:border-[#8AFF45] focus:outline-none"
              />
              {draft.trim() && slugifyProjectName(draft) !== draft.trim() && (
                <p className="mt-1.5 text-[10px] text-neutral-600">
                  Folder：<span className="text-neutral-400">
                    {slugifyProjectName(draft) || 'Untitled'}
                  </span>
                </p>
              )}
              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={close}
                  className="rounded-md px-2 py-1 text-[11px] text-neutral-500 hover:text-neutral-300"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={() =>
                    void (mode === 'rename' ? submitRename() : submitCreate())
                  }
                  disabled={!draft.trim()}
                  className="rounded-md bg-[#8AFF45] px-3 py-1 text-[11px] font-medium text-[#0d0d12] disabled:cursor-not-allowed disabled:bg-[#222230] disabled:text-neutral-600"
                >
                  確認
                </button>
              </div>
            </div>
          )}

          {error && (
            <p className="mt-1 px-2 py-1 text-[11px] text-[#ff8a8a]">{error}</p>
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  onClick,
  children,
  disabled,
  className,
}: {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`block w-full rounded-sm px-2 py-1.5 text-left text-[12px] text-neutral-300 hover:bg-[#1a1a24] disabled:cursor-not-allowed disabled:opacity-50 ${className ?? ''}`}
    >
      {children}
    </button>
  );
}

function Separator() {
  return <div className="my-1 h-px bg-[#222230]" />;
}
